import { Injectable } from '@angular/core';
import { TealiumAnalyticsEvent, TealiumAnalyticsService } from '@ddt-web/analytics';
import { contentConfigFetched, InjectConfig, IqConfigKey } from '@ddt-web/config-layer';
import { ApiService } from '@ddt-web/data-service';
import { fundPreferenceChildMissingPerms, fundPreferenceMissingPerms } from '@ddt-web/fund-preferences';
import { partialSecurityFailing } from '@ddt-web/setup-data-workers';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { filter, map, switchMap, tap } from 'rxjs/operators';

import { FundStatusInteractionData } from '../models';
import { FundStatusInteractionStoreService } from '../services/fund-status-interaction-store.service';
import { FundStatusInteractionService } from '../services/fund-status-interaction.service';
import { upsertFundStats } from './fund-stats.actions';

@Injectable()
export class FundStatusInteractionEffects {
	@InjectConfig(IqConfigKey.ENABLE_FUND_STATUS_INTERACTIONS) private readonly enableStatusInteractions = false;

	constructor(
		private actions$: Actions,
		private fundStatusInteractionService: FundStatusInteractionService,
		private fundStatusInteractionStoreService: FundStatusInteractionStoreService,
		private apiService: ApiService,
		private tealiumAnalyticsService: TealiumAnalyticsService
	) { }

	public fetchAllFundStatusInteractions$ = createEffect(() =>
		this.actions$.pipe(
			ofType(contentConfigFetched),
			filter(() => this.enableStatusInteractions),
			switchMap(() => this.fundStatusInteractionService.getAllFundStatusInteractions()),
			tap((fundStatusInteractions: FundStatusInteractionData[]) => this.fundStatusInteractionStoreService.setFundStatusInteraction(fundStatusInteractions)),
			tap((fundStatusInteractions: FundStatusInteractionData[]) => this.trackAnalysis(fundStatusInteractions))
		), { dispatch: false }
	);

	public checkForFundStatusInteractionUpdates$ = createEffect(() =>
		this.actions$.pipe(
			ofType(upsertFundStats),
			filter(() => this.enableStatusInteractions),
			switchMap(() => this.fundStatusInteractionService.checkFundStatusInteractionsForUpdates())
		), { dispatch: false }
	);

	public applyMissingWudPermsToChildren$ = createEffect(() =>
		this.actions$.pipe(
			ofType(fundPreferenceChildMissingPerms),
			map(action => action.payload),
			filter(payload => payload.store === 'fundStatusInteractions'),
			switchMap((payload: {
				store: string;
				childrenMissingWudPerms: Set<string>;
				fundPreferences: any;
			}) => {
				const fundPreferencesById = payload.fundPreferences.reduce((fundPreferencesById, child) => {
					if (payload.childrenMissingWudPerms.has(child.id)) {
						fundPreferencesById[child.id] = child;
					}
					return fundPreferencesById;
				}, {});
				return this.fundStatusInteractionService.editPreferences(fundPreferencesById, fundPreferencesById, payload.store);
			})
		), { dispatch: false }
	);

	public applyMissingWudPermsToParent$ = createEffect(() =>
		this.actions$.pipe(
			ofType(fundPreferenceMissingPerms),
			map(action => action.payload),
			filter(payload => payload.store === 'fundStatusInteractions'),
			switchMap(() => {
				return this.apiService.addPreference({
					preferenceKey: 'BFA_FUND_STATUS_INTERACTIONS',
					type: 'PREFERENCE',
					applications: ['BFA']
				});
			})
		), { dispatch: false }
	);

	public cleanUpFundStatusInteractionsForMissingFunds$ = createEffect(() =>
		this.actions$.pipe(
			ofType(partialSecurityFailing),
			map(action => action.payload),
			switchMap((errorCodes) => this.fundStatusInteractionService.removeInteractionsForMissingSecurities(Array.from(errorCodes.keys())))
		), { dispatch: false }
	);

	private trackAnalysis(fundStatusInteractions: FundStatusInteractionData[]): void {
		const interactionStats = Object.entries(fundStatusInteractions.reduce((interactionsByInteractionType, interaction) => {
			interactionsByInteractionType[interaction.interactionType].push(interaction);
			return interactionsByInteractionType;
		}, { dismiss: [], accept: [] })).map(([interactionType, interactions]: [string, FundStatusInteractionData[]]) => `${interactionType}:${interactions.length}`);

		this.tealiumAnalyticsService.trackEventTealium(
			new TealiumAnalyticsEvent('iq analysis:fund health stats', { fundHealthAnalysisBreakdown: interactionStats.join('|') })
		);
	}
}
